import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import FolderTabs from '../../components/common/FolderTabs';
import SwitchField from '../../components/common/SwitchField';
import CommunicationNotificationsPanel from '../../components/stores/CommunicationNotificationsPanel';
import CommunicationWaitlistPanel from '../../components/stores/CommunicationWaitlistPanel';
import { useToast } from '../../context/ToastContext';
import StoreService from '../../services/storeService';
import usePermissions from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { Permissions } from '../../constants/Permissions';
import ProCrownButton from '../../components/billing/ProCrownButton';

const SENDER_NAME_MAX_LENGTH = 15;
const SENDER_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 ]*$/;
const NOTIFICATIONS_TAB = 'notifications';
const WAITLIST_TAB = 'waitlist';

const truncateSmsSenderName = (value) => (value || '').trim().slice(0, SENDER_NAME_MAX_LENGTH).trimEnd();

export const resolveCommunicationTab = (tabParam) => (
  tabParam === WAITLIST_TAB ? WAITLIST_TAB : NOTIFICATIONS_TAB
);

const normalizeSettings = (result) => ({
  confirmationSmsEnabled: Boolean(result?.confirmationSmsEnabled ?? result?.ConfirmationSmsEnabled),
  cancellationSmsEnabled: Boolean(result?.cancellationSmsEnabled ?? result?.CancellationSmsEnabled),
  confirmationPushEnabled: Boolean(result?.confirmationPushEnabled ?? result?.ConfirmationPushEnabled ?? true),
  cancellationPushEnabled: Boolean(result?.cancellationPushEnabled ?? result?.CancellationPushEnabled ?? true),
  reminder7DayEmailEnabled: Boolean(result?.reminder7DayEmailEnabled ?? result?.Reminder7DayEmailEnabled),
  reminder7DaySmsEnabled: Boolean(result?.reminder7DaySmsEnabled ?? result?.Reminder7DaySmsEnabled),
  reminder7DayPushEnabled: Boolean(result?.reminder7DayPushEnabled ?? result?.Reminder7DayPushEnabled),
  reminder1DayEmailEnabled: Boolean(result?.reminder1DayEmailEnabled ?? result?.Reminder1DayEmailEnabled),
  reminder1DaySmsEnabled: Boolean(result?.reminder1DaySmsEnabled ?? result?.Reminder1DaySmsEnabled),
  reminder1DayPushEnabled: Boolean(result?.reminder1DayPushEnabled ?? result?.Reminder1DayPushEnabled ?? true),
  reminder2HourEmailEnabled: Boolean(result?.reminder2HourEmailEnabled ?? result?.Reminder2HourEmailEnabled),
  reminder2HourSmsEnabled: Boolean(result?.reminder2HourSmsEnabled ?? result?.Reminder2HourSmsEnabled),
  reminder2HourPushEnabled: Boolean(result?.reminder2HourPushEnabled ?? result?.Reminder2HourPushEnabled ?? true),
  googleReviewAskEmailEnabled: Boolean(result?.googleReviewAskEmailEnabled ?? result?.GoogleReviewAskEmailEnabled),
  googleReviewAskSmsEnabled: Boolean(result?.googleReviewAskSmsEnabled ?? result?.GoogleReviewAskSmsEnabled),
  googleReviewAskPushEnabled: Boolean(result?.googleReviewAskPushEnabled ?? result?.GoogleReviewAskPushEnabled),
  smsSenderName: result?.smsSenderName ?? result?.SmsSenderName ?? '',
  waitlistType: result?.waitlistType ?? result?.WaitlistType ?? 'Manual',
  waitlistPriority: result?.waitlistPriority ?? result?.WaitlistPriority ?? 'FirstInLine',
});

const normalizeSmsSettings = (result) => ({
  canSend: Boolean(result?.canSend ?? result?.CanSend),
  canEnable: Boolean(result?.canEnable ?? result?.CanEnable),
  enabled: Boolean(result?.enabled ?? result?.Enabled),
  unitPrice: Number(result?.unitPrice ?? result?.UnitPrice ?? 0.03),
  currency: result?.currency ?? result?.Currency ?? 'EUR',
});

const CommunicationSettingsPage = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isOwner } = usePermissions(storeId);
  const { isPro, entitlements } = useStorePlan(storeId, Boolean(storeId));
  const allowsSms = isPro && entitlements?.allowsSms !== false;
  const allowsWaitlist = isPro && entitlements?.allowsWaitlist !== false;
  const lockProFeatures = Boolean(entitlements) && !isPro;
  const { showSuccess, showError } = useToast();
  const activeTab = allowsWaitlist ? resolveCommunicationTab(searchParams.get('tab')) : NOTIFICATIONS_TAB;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [smsSettings, setSmsSettings] = useState(null);
  const [storeName, setStoreName] = useState('');
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderNameError, setSenderNameError] = useState('');

  const canManage = hasPermission(Permissions.StoresNotificationSettingsUpdate) || isOwner;
  const smsBillingActive = smsSettings?.canSend === true;
  const hasGoogleReviewUrl = Boolean(googleReviewUrl && String(googleReviewUrl).trim());
  const defaultSenderName = truncateSmsSenderName(storeName);

  const tabs = useMemo(() => ([
    { value: NOTIFICATIONS_TAB, label: t('communicationSettingsPage.tabs.notifications') },
    { value: WAITLIST_TAB, label: t('communicationSettingsPage.tabs.waitlist'), proLocked: lockProFeatures },
  ]), [lockProFeatures, t]);

  const loadData = useCallback(async () => {
    if (!storeId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [notifResult, smsResult, storeResult] = await Promise.all([
        StoreService.getNotificationSettings(storeId),
        StoreService.getSmsSettings(storeId).catch(() => null),
        StoreService.getStore(storeId).catch(() => null),
      ]);
      const normalized = normalizeSettings(notifResult);
      setSettings(normalized);
      setSenderName(normalized.smsSenderName ?? '');
      setSmsSettings(normalizeSmsSettings(smsResult));
      setStoreName(storeResult?.name ?? storeResult?.Name ?? '');
      setGoogleReviewUrl(storeResult?.googleReviewUrl ?? storeResult?.GoogleReviewUrl ?? '');
    } catch (err) {
      console.error('Failed to load notification settings:', err);
      showError(t('communicationSettingsPage.error.load'));
    } finally {
      setLoading(false);
    }
  }, [showError, storeId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateField = useCallback((field, value) => {
    setSettings((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'reminder7DayEmailEnabled' && !value) next.reminder7DaySmsEnabled = false;
      if (field === 'reminder1DayEmailEnabled' && !value) next.reminder1DaySmsEnabled = false;
      if (field === 'reminder2HourEmailEnabled' && !value) next.reminder2HourSmsEnabled = false;
      if (field === 'waitlistType' && value !== 'Auto') next.waitlistPriority = prev.waitlistPriority || 'FirstInLine';
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (!settings) return;

    const anySms = allowsSms && (
      settings.confirmationSmsEnabled
      || settings.cancellationSmsEnabled
      || settings.reminder7DaySmsEnabled
      || settings.reminder1DaySmsEnabled
      || settings.reminder2HourSmsEnabled
      || settings.googleReviewAskSmsEnabled
    );

    if ((settings.googleReviewAskEmailEnabled || settings.googleReviewAskSmsEnabled) && !hasGoogleReviewUrl) {
      showError(t('communicationSettingsPage.googleReviewAsk.missingUrl'));
      return;
    }

    if (anySms && !smsBillingActive) {
      showError(t('communicationSettingsPage.smsDisabledHelp'));
      return;
    }

    const trimmed = allowsSms ? senderName.trim() : '';
    if (allowsSms && trimmed && !SENDER_NAME_PATTERN.test(trimmed)) {
      setSenderNameError(t('communicationSettingsPage.smsSenderName.error.invalid'));
      return;
    }
    setSenderNameError('');

    try {
      setSaving(true);
      await StoreService.updateNotificationSettings(storeId, {
        ...settings,
        ...(allowsSms ? {} : {
          confirmationSmsEnabled: false,
          cancellationSmsEnabled: false,
          reminder7DaySmsEnabled: false,
          reminder1DaySmsEnabled: false,
          reminder2HourSmsEnabled: false,
          googleReviewAskSmsEnabled: false,
        }),
        smsSenderName: trimmed || null,
      });
      showSuccess(t('communicationSettingsPage.success.save'));
    } catch (err) {
      console.error('Failed to save notification settings:', err);
      showError(err?.response?.data?.message || t('communicationSettingsPage.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const renderSmsToggle = (field, extraDisabled = false) => {
    if (!allowsSms) {
      return (
        <SwitchField
          label={(
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              {t('communicationSettingsPage.smsLabel')}
              <ProCrownButton locked size="tiny" />
            </Box>
          )}
          checked={false}
          onChange={() => {}}
          disabled
          compact
        />
      );
    }
    const emailField = field.replace('Sms', 'Email');
    const emailOn = field.includes('reminder') ? Boolean(settings?.[emailField]) : true;
    const currentlyOn = Boolean(settings?.[field]);
    const isDisabled = saving || !allowsSms || !emailOn || extraDisabled || (!smsBillingActive && !currentlyOn);
    const tooltip = !allowsSms
      ? t('storePlan.smsUpgradeMessage')
      : !smsBillingActive && !currentlyOn
      ? t('communicationSettingsPage.smsDisabledHelp')
      : !emailOn
        ? t('communicationSettingsPage.smsDependsOnEmail')
        : '';

    return (
      <Tooltip title={tooltip} placement="top" disableHoverListener={!tooltip}>
        <span>
          <SwitchField
            label={t('communicationSettingsPage.smsLabel')}
            checked={Boolean(settings?.[field])}
            onChange={(e) => updateField(field, e.target.checked)}
            disabled={isDisabled}
            compact
          />
        </span>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <ManagementPageLayout pageTitle={t('communicationSettingsPage.title')} pageSubtitle={t('communicationSettingsPage.subtitle')}>
        <ManagementPageSkeleton />
      </ManagementPageLayout>
    );
  }

  if (!canManage) {
    return (
      <ManagementPageLayout pageTitle={t('communicationSettingsPage.title')} pageSubtitle={t('communicationSettingsPage.subtitle')}>
        <Alert severity="warning">{t('common.unauthorized')}</Alert>
      </ManagementPageLayout>
    );
  }

  return (
    <ManagementPageLayout pageTitle={t('communicationSettingsPage.title')} pageSubtitle={t('communicationSettingsPage.subtitle')}>
      <FolderTabs
        tabs={tabs}
        value={activeTab}
        onChange={(nextTab) => setSearchParams(nextTab === NOTIFICATIONS_TAB ? {} : { tab: nextTab }, { replace: true })}
        contentId="communication-settings-tabs-content"
        equalWidthOnMobile
      >
        <div role="tabpanel" hidden={activeTab !== NOTIFICATIONS_TAB}>
          {activeTab === NOTIFICATIONS_TAB ? (
            <CommunicationNotificationsPanel
              storeId={storeId}
              settings={settings}
              allowsSms={allowsSms}
              smsBillingActive={smsBillingActive}
              smsUnitPrice={smsSettings?.unitPrice}
              smsCurrency={smsSettings?.currency}
              hasGoogleReviewUrl={hasGoogleReviewUrl}
              senderName={senderName}
              senderNameError={senderNameError}
              defaultSenderName={defaultSenderName}
              saving={saving}
              loading={loading}
              isOwner={isOwner}
              onSenderNameChange={setSenderName}
              onSenderNameErrorChange={setSenderNameError}
              onFieldChange={updateField}
              onSave={handleSave}
              renderSmsToggle={renderSmsToggle}
            />
          ) : null}
        </div>
        {allowsWaitlist ? (
        <div role="tabpanel" hidden={activeTab !== WAITLIST_TAB}>
          {activeTab === WAITLIST_TAB ? (
            <CommunicationWaitlistPanel
              settings={settings}
              saving={saving}
              loading={loading}
              onFieldChange={updateField}
              onSave={handleSave}
            />
          ) : null}
        </div>
        ) : null}
      </FolderTabs>
    </ManagementPageLayout>
  );
};

export default CommunicationSettingsPage;
